import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, XAxis, YAxis, CartesianGrid, Bar } from "recharts";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Reports() {
  const [d, setD] = useState({});
  useEffect(() => {
    (async () => {
      const [inv, exp, proj, emp, itm] = await Promise.all([
        api.get("/invoices"), api.get("/expenses"), api.get("/projects"),
        api.get("/employees"), api.get("/inventory"),
      ]);
      setD({ invoices: inv.data, expenses: exp.data, projects: proj.data, employees: emp.data, inventory: itm.data });
    })();
  }, []);

  const invStatus = ["paid","pending","overdue"].map((s) => ({ name: s, value: (d.invoices||[]).filter(i => i.status===s).reduce((a,b)=>a+(b.total||0),0) }));
  const expByCat = Object.entries((d.expenses||[]).reduce((acc,e) => { acc[e.category] = (acc[e.category]||0) + e.amount; return acc; }, {})).map(([k,v]) => ({ name: k, value: v }));
  const COLORS = ["hsl(var(--primary))","hsl(var(--accent))","hsl(var(--chart-3))","hsl(var(--chart-4))","hsl(var(--chart-5))"];

  const total_rev = (d.invoices||[]).filter(i=>i.status==="paid").reduce((a,b)=>a+b.total,0);
  const total_exp = (d.expenses||[]).reduce((a,b)=>a+b.amount,0);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Analytics</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">Reports</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={fmt(total_rev)} />
        <StatCard label="Expenses" value={fmt(total_exp)} />
        <StatCard label="P&L Margin" value={total_rev ? Math.round(((total_rev - total_exp)/total_rev)*100) + "%" : "—"} />
        <StatCard label="Projects" value={(d.projects||[]).length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl p-6 bg-card border border-border/60">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4">Invoice Status</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={invStatus} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100}>
                {invStatus.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Legend />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background:"hsl(var(--card))", border:"1px solid hsl(var(--border))", borderRadius:8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl p-6 bg-card border border-border/60">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4">Expenses by Category</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={expByCat}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background:"hsl(var(--card))", border:"1px solid hsl(var(--border))", borderRadius:8 }} />
              <Bar dataKey="value" fill="hsl(var(--accent))" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl p-5 bg-card border border-border/60">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}
